import { useSelector } from 'react-redux';

import { RootState } from '../redux/rootReducer';
import { BookAddPayload, generateBookId } from '../redux/bookDbSlice';
import { SheetAddPayload, generateSheetId } from '../redux/sheetDbSlice';
import { SheetInformationAddPayload } from '../redux/sheetInformationDbSlice';
import { CardDb, CardAddPayload, generateCardId, isStoryCard, isMapCard, isGridCard } from '../redux/cardDbSlice';
import { CardAttributeAddPayload } from '../redux/cardAttributeDbSlice';
import { CardShapeAddPayload } from '../redux/cardShapeDbSlice';
import { MemoCardContentAddPayload } from '../redux/memoCardContentDbSlice';
import { UiCardItemAddPayload, generateUiCardItemId } from '../redux/uiCardItemDbSlice';
import { DrawCardContentAddPayload } from '../redux/drawCardContentDbSlice';
import { ArrowDb } from '../redux/arrowDbSlice';
import { RelationUserBookAddPayload } from '../redux/relationUserBookDbSlice';
import { RelationFolderBookAddPayload } from '../redux/relationFolderBookDbSlice';
import { SheetTreeAddPayload } from '../redux/sheetTreeDbSlice';
import useCurrentTree from './useCurrentTree';
import useCurrentUserId from './useCurrentUserId';
import useCurrentFolderId from './useCurrentFolderId';
import { SheetTree } from './useCurrentTree';
import useAllBookDbsInFolder from './useAllBookDbsInFolder';
import useOneBookDb from './useOneBookDb';
import useAllCardDbsInBook from '../hooks/useAllCardDbsInBook';
import { Hash, deepCopy } from '../general';

type AddPayloads = {
  book: BookAddPayload;
  relationUserBook: RelationUserBookAddPayload;
  relationFolderBook: RelationFolderBookAddPayload;
  sheets: SheetAddPayload[];
  sheetInformations: SheetInformationAddPayload[];
  sheetTrees: SheetTreeAddPayload[];
  cards: CardAddPayload[];
  cardAttributes: CardAttributeAddPayload[];
  cardShapes : CardShapeAddPayload[];
  memoContents: MemoCardContentAddPayload[];
  uiItems: UiCardItemAddPayload[];
  drawContents: DrawCardContentAddPayload[];
  arrows: ArrowDb[];
}
const useCopyBook = (bookId: string): AddPayloads | undefined => {
  const sheetTree = useCurrentTree(bookId);
  const userId = useCurrentUserId();
  const folderId = useCurrentFolderId();
  const sheetDb = useSelector((state: RootState) => state.sheetDb);
  const sheetInformationDb = useSelector((state: RootState) => state.sheetInformationDb);
  const cardAttributeDb = useSelector((state: RootState) => state.cardAttributeDb);
  const cardShapeDb = useSelector((state: RootState) => state.cardShapeDb);
  const memoCardContentDb = useSelector((state: RootState) => state.memoCardContentDb);
  const uiCardItemDb = useSelector((state: RootState) => state.uiCardItemDb);
  const drawCardContentDb = useSelector((state: RootState) => state.drawCardContentDb);
  const arrowDb = useSelector((state: RootState) => state.arrowDb);
  const [bookIds] = useAllBookDbsInFolder(folderId);
  const bookValue = useOneBookDb(bookId);
  const [cardIdsInBook, cardsInBook] = useAllCardDbsInBook(bookId);

  const newBookId = generateBookId();
  const copiedBook: BookAddPayload = bookValue ? {id: newBookId, name: bookValue.name + 'のコピー', notes: bookValue.notes, coverImage: bookValue.coverImage} : {id: newBookId, name: '', notes: 'ブック説明'};
  const copiedRelationUserBook: RelationUserBookAddPayload = {userId: userId || 'noUser', bookId: newBookId};
  const copiedRelationFolderBook: RelationFolderBookAddPayload = {userId: userId || 'noUser', folderId: folderId || 'noFolder', bookId: newBookId, order: bookIds.length};

  const copiedSheets: SheetAddPayload[] = [];
  const copiedSheetInformtions: SheetInformationAddPayload[] = [];
  const copiedSheetTrees: SheetTreeAddPayload[] = [];
  const copiedCards: CardAddPayload[] = [];
  const copiedCardAttributes: CardAttributeAddPayload[] = [];
  const copiedCardShapes: CardShapeAddPayload[] = [];
  const copiedMemoContents: MemoCardContentAddPayload[] = [];
  const copiedUiItems: UiCardItemAddPayload[] = [];
  const copiedDrawContents: DrawCardContentAddPayload[] = [];
  const copiedArrows: ArrowDb[] = [];

  type CopyingArrowsType = ArrowDb & { fromDone: boolean; toDone: boolean; };
  const getCopyingArrows = (sheetId: string, newSheetId: string) => {
    const originArrowIds = Object.keys(arrowDb.arrows).filter(id => arrowDb.arrows[id].sheetId === sheetId);
    const copyingArrows: CopyingArrowsType[] = originArrowIds.map(id => {
      return { ...arrowDb.arrows[id], sheetId: newSheetId, fromDone: false, toDone: false };
    });
    return copyingArrows;
  }
  let copyingArrows: CopyingArrowsType[] = [];

  const copyCardContent = (cardId: string, card: CardDb, newCardId: string): void => {
    if (card.kind === 'common') {
      // copy card attributes
      const cardAttributeIds = Object.keys(cardAttributeDb.cardAttributes).filter(attributeId => cardAttributeDb.cardAttributes[attributeId].cardId === cardId);
      const cardAttributes = cardAttributeIds.map(attributeId => {
        const originalAttribute = cardAttributeDb.cardAttributes[attributeId];
        return {...originalAttribute, cardId: newCardId};
      });
      copiedCardAttributes.push(...cardAttributes);
      // copy card shapes
      const cardShapeIds = Object.keys(cardShapeDb.cardShapes).filter(id => cardShapeDb.cardShapes[id].cardId === cardId);
      const cardShapes = cardShapeIds.map(id => {
        const originalShape = cardShapeDb.cardShapes[id];
        return {...originalShape, cardId: newCardId};
      })
      copiedCardShapes.push(...cardShapes);
    } else if (card.kind === 'memo') {
      // copy memo card content
      const ids = Object.keys(memoCardContentDb.memoCardContents).filter(id => memoCardContentDb.memoCardContents[id].cardId === cardId);
      const originalValue = memoCardContentDb.memoCardContents[ids[0]];
      copiedMemoContents.push({...originalValue, cardId: newCardId});
    } else if (card.kind === 'ui') {
      // copy ui card items
      const uiItemIds = Object.keys(uiCardItemDb.uiCardItems).filter(id => uiCardItemDb.uiCardItems[id].cardId === cardId);
      const newIdList: { [key in string]: string } = {};
      uiItemIds.forEach(id => {
        const newId = generateUiCardItemId();
        newIdList[id] = newId;
      })
      const uiItems = uiItemIds.map(id => {
        const originalUiItem = uiCardItemDb.uiCardItems[id];
        if (originalUiItem.parentId === cardId) return { ...originalUiItem, id: newIdList[id], cardId: newCardId, parentId: newCardId };
        else return { ...originalUiItem, id: newIdList[id], cardId: newCardId, parentId: newIdList[originalUiItem.parentId] };
      });
      copiedUiItems.push(...uiItems);
    } else if (card.kind === 'draw') {
      // copy draw card content
      const ids = Object.keys(drawCardContentDb.drawCardContents).filter(id => drawCardContentDb.drawCardContents[id].cardId === cardId);
      const originalValue = drawCardContentDb.drawCardContents[ids[0]];
      copiedDrawContents.push({...originalValue, cardId: newCardId});
    }
  }
  const copyArrows = (cardId: string, newCardId: string) => {
    if (copyingArrows.length === 0) return;
    const newCopyingArrows = deepCopy(copyingArrows);
    copyingArrows.forEach((arrowData, ix) => {
      let { fromDone, toDone, ...arrowValue } = arrowData;
      const arrow = deepCopy(arrowValue);
      if (fromDone && toDone) return;
      if (arrow.fromId === cardId) {
        arrow.fromId = newCardId;
        fromDone = true;
      } else if (arrow.toId === cardId) {
        arrow.toId = newCardId;
        toDone = true;
      } else return;
      newCopyingArrows[ix] = { ...arrow, fromDone, toDone };
      if (fromDone && toDone) copiedArrows.push(arrow);
    })
    copyingArrows = newCopyingArrows;
  }
  const copyCards = (sheetId: string, newSheetId: string) => {
    const idConvertList: Hash<string> = {};
    const cardList: Hash<CardDb> = {};
    cardIdsInBook.forEach((id, ix) => {
      const card = cardsInBook[ix];
      if (card.sheetId === sheetId) {
        idConvertList[id] = generateCardId();
        cardList[id] = card;
      }
    })
    const cardIds = Object.keys(idConvertList);
    cardIds.forEach(id => {
      const card = cardList[id];
      const newCardId = idConvertList[id];
      if (!card || !newCardId) return;
      if (isStoryCard(card) && card.parentId) copiedCards.push({ ...card, id: newCardId, sheetId: newSheetId, parentId: idConvertList[card.parentId] });
      else if (isMapCard(card) && card.groupParent) copiedCards.push({ ...card, id: newCardId, sheetId: newSheetId, groupParent: idConvertList[card.groupParent] });
      else copiedCards.push({ ...card, id: newCardId, sheetId: newSheetId });

      // copy card content
      copyCardContent(id, card, newCardId);
      // copy arrows
      if (isMapCard(card) || isGridCard(card)) copyArrows(id, newCardId);
    })
  }

  const copyChildren = (tree: SheetTree[], parentId?: string) : void => {
    tree.forEach((sheet, ix) => {
      const sheetValue = sheetDb.sheets[sheet.id];
      const newId = generateSheetId();
      // copy sheet
      copiedSheets.push({...sheetValue, id: newId});
      // copy sheet information
      const sheetInformationIds =
        Object.keys(sheetInformationDb.sheetInformations).filter(id => sheetInformationDb.sheetInformations[id].sheetId === sheet.id);
      const originalSheetInformations = sheetInformationIds.map(id => sheetInformationDb.sheetInformations[id]);
      originalSheetInformations.forEach(value => {
        copiedSheetInformtions.push({...value, sheetId: newId});
      });
      // copy sheet tree
      if (parentId) copiedSheetTrees.push({bookId: newBookId, sheetId: newId, parentSheetId: parentId, order: ix});
      else copiedSheetTrees.push({bookId: newBookId, sheetId: newId, order: ix});
      // prepare arrows
      copyingArrows.push(...getCopyingArrows(sheet.id, newId));
      // copy card
      copyCards(sheet.id, newId);

      if (sheet.children) {
        // copy children sheet recursively
        copyChildren(sheet.children, newId);
      }
    })
  }
  if (sheetTree?.children) {
    copyChildren(sheetTree?.children);
    return {
      book: copiedBook,
      relationUserBook: copiedRelationUserBook,
      relationFolderBook: copiedRelationFolderBook,
      sheets: copiedSheets,
      sheetInformations: copiedSheetInformtions,
      sheetTrees: copiedSheetTrees,
      cards: copiedCards,
      cardAttributes: copiedCardAttributes,
      cardShapes: copiedCardShapes,
      memoContents: copiedMemoContents,
      uiItems: copiedUiItems,
      drawContents: copiedDrawContents,
      arrows: copiedArrows
    };
  } else return undefined;
}

export default useCopyBook;
